import {
  MAX_PAGE_SIZE,
  type UsersListQuerySchema,
  type Paginated,
  type User,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { type SelectionOption } from '@/shared/components/selection/types/selection-option.types';
import {
  type ServerSelectQueryArgs,
  type UseServerSelectQuery,
  type UseServerSelectResolve,
} from '@/shared/components/selection/types/server-selection.types';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/** The wire shape — see `role-lookup.api.ts` for why this is `z.input`. */
type UserLookupParams = z.input<typeof UsersListQuerySchema>;

/**
 * The **cross-module read bridge** for users (W6).
 *
 * Every later phase needs to name a member from outside `modules/Members`: an
 * audit row's actor, a violation's subject, a meeting's participants, a
 * conversation's two sides. Each of those is a different module, and without
 * this bridge each would import the members api and couple itself to a module it
 * has no business knowing.
 */
export const userLookupApi = createApi({
  reducerPath: 'userLookupApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['UserLookup'],
  endpoints: (builder) => ({
    lookupUsers: builder.query<Paginated<User>, UserLookupParams>({
      query: (params) => ({ url: '/admin/users', method: 'GET', params }),
      providesTags: ['UserLookup'],
    }),
  }),
});

export const { useLookupUsersQuery } = userLookupApi;

function toItem(user: User): SelectionOption {
  // The email, because it is the only human-readable identity a staff user has
  // on this endpoint — a member's display name lives behind the question engine
  // and the visibility resolver (N18), which a lookup must not reach around.
  return { value: user.id, label: user.email, description: user.status };
}

const EMPTY: readonly SelectionOption[] = [];

/**
 * The MEMBER picker (P27c1) — every consumer chooses a member: who receives a
 * grant, whose order this is, whose delivery to inspect. So the bridge says
 * `isStaff=false` on every request, exactly as the staff list says `true`;
 * until it did, the entitlements picker listed the staff accounts. A staff
 * picker does not exist because no surface needs one — the day one does, it
 * is a second hook, never a flag on this one.
 */
export const useMemberOptionsQuery: UseServerSelectQuery = ({
  search,
  page,
  pageSize,
}: ServerSelectQueryArgs) => {
  const { data, isFetching, isError, refetch } = useLookupUsersQuery({
    page,
    pageSize,
    order: 'desc',
    isStaff: 'false',
    ...(search === '' ? {} : { search }),
  });

  return {
    items: data === undefined ? EMPTY : data.items.map(toItem),
    hasNext: data?.meta.hasNext ?? false,
    isFetching,
    isError,
    // So a failed load offers a way out rather than a dead-end message (U0).
    refetch,
  };
};

/**
 * Kind-agnostic on purpose: it resolves ids the operator is already looking at
 * — an actor on an audit row, a party on a booking — and narrowing it to
 * members would blank the actor column on every staff act.
 */
export const useResolveUserIds: UseServerSelectResolve = (ids: readonly string[]) => {
  const { data, isFetching } = useLookupUsersQuery(
    {
      page: 1,
      // Clamped for the reason `role-lookup.api.ts` spells out: the api refuses
      // an over-large page rather than clamping it (N7).
      pageSize: Math.min(Math.max(ids.length, 1), MAX_PAGE_SIZE),
      order: 'desc',
      ids: ids.join(','),
    },
    { skip: ids.length === 0 },
  );

  return { items: data === undefined ? EMPTY : data.items.map(toItem), isFetching };
};
